/**
 * The Incident Report (client notes 10/26, Settings 1d): "there should always be an Incident Report (IR) created when a
 * campaign or incident is closed". The backend has no report to read, so the console writes it from the records of the
 * feed the moment a campaign or an incident reaches Closed, and writes nothing that the records do not say: what came in,
 * who and what it touched, how it was remediated, the steps of the plan and when each step happened. Pure, so a test can
 * state what a report holds.
 */
import { campaignLabel, incidentLabel } from './ids';
import { formatDurationMs } from './funnel';
import { sourceLabel } from './sourceNames';
import { formatUtcDateTime } from './time';
import { stepLabel } from '../data/selectors/campaignView';
import { FEED_ACTION_LABEL, threatActionType, type FeedRow, type Severity, type ThreatRowView } from '../data/selectors/feed';

export interface ReportIncident {
  label: string;
  title: string;
  severity: Severity;
  /** How it was remediated: Automated, or Gated (a person acted). */
  action: string;
  /** The steps of its remediation plan, in order. */
  steps: string[];
}

export interface ReportEvent {
  atMs: number;
  /** What happened, with the incident's label when the report holds more than one. */
  text: string;
}

export interface IncidentReport {
  /** The id of the feed row, which is the id of the campaign or the lone incident. */
  id: string;
  label: string;
  kind: 'campaign' | 'incident';
  title: string;
  description: string;
  severity: Severity;
  sources: string[];
  assets: string[];
  /** Automated, Gated, or both when a campaign mixes them. */
  action: string;
  incidents: ReportIncident[];
  receivedMs: number | null;
  closedMs: number | null;
  /** From the first alert to the check that closed the last incident. */
  timeToCloseMs: number | null;
  timeline: ReportEvent[];
}

function stamp(value: unknown): number | null {
  const ms = Date.parse(String(value ?? ''));
  return Number.isFinite(ms) ? ms : null;
}

function eventsOf(member: ThreatRowView, prefix: string): ReportEvent[] {
  const plan = member.threat.plan;
  const mode = threatActionType(member.threat);
  const entries: [number | null, string][] = [
    [member.firstEventMs, 'Alert received'],
    [stamp(plan?.formed_at), 'Remediation plan formed'],
    [stamp(plan?.assigned_at), 'Assigned'],
    [stamp(plan?.decided_at), mode === 'gated' ? 'Approved by a person' : 'Execution started'],
    [stamp(plan?.verified_at), 'Remediation verified, incident closed'],
  ];
  return entries
    .filter((entry): entry is [number, string] => entry[0] != null)
    .map(([atMs, text]) => ({ atMs, text: `${prefix}${text}` }));
}

export function buildIncidentReport(row: FeedRow): IncidentReport {
  const campaign = row.kind === 'campaign';
  const incidents: ReportIncident[] = row.members.map((member) => {
    const operations = member.threat.plan?.operations;
    return {
      label: incidentLabel(member.threat.event._id),
      title: member.title,
      severity: member.severity,
      action: FEED_ACTION_LABEL[threatActionType(member.threat)],
      steps: (Array.isArray(operations) ? operations : []).map(stepLabel).filter(Boolean),
    };
  });
  const timeline = row.members
    .flatMap((member) => eventsOf(member, campaign ? `${incidentLabel(member.threat.event._id)}: ` : ''))
    .sort((a, b) => a.atMs - b.atMs);
  const closes = row.members.map((member) => stamp(member.threat.plan?.verified_at)).filter((ms): ms is number => ms != null);
  const closedMs = closes.length > 0 ? Math.max(...closes) : row.lastUpdatedMs;
  const actions = Array.from(new Set(incidents.map((incident) => incident.action)));
  return {
    id: row.id,
    label: campaign ? campaignLabel(row.campaign?._id || row.id) : incidentLabel(row.id),
    kind: campaign ? 'campaign' : 'incident',
    title: row.title,
    description: row.description,
    severity: row.severity,
    sources: row.sources.map(sourceLabel),
    assets: row.entities.map((entity) => entity.name),
    action: actions.join(' and '),
    incidents,
    receivedMs: row.firstEventMs,
    closedMs,
    timeToCloseMs: row.firstEventMs != null && closedMs != null && closedMs >= row.firstEventMs ? closedMs - row.firstEventMs : null,
    timeline,
  };
}

/** One report for each campaign or incident that is Closed, the most recently closed first. */
export function closedReports(rows: FeedRow[]): IncidentReport[] {
  return rows
    .filter((row) => row.state.kind === 'closed')
    .map(buildIncidentReport)
    .sort((a, b) => (b.closedMs ?? 0) - (a.closedMs ?? 0));
}

/** The report as a Markdown document, to keep or to send. */
export function reportToMarkdown(report: IncidentReport): string {
  const lines: string[] = [`# Incident Report ${report.label}: ${report.title}`, ''];
  lines.push(`- Severity: ${report.severity}`);
  lines.push(`- Type: ${report.kind === 'campaign' ? 'Campaign' : 'Incident'}`);
  lines.push(`- Remediated: ${report.action || '—'}`);
  lines.push(`- Sources: ${report.sources.join(', ') || '—'}`);
  lines.push(`- Assets affected: ${report.assets.join(', ') || '—'}`);
  lines.push(`- First alert: ${formatUtcDateTime(report.receivedMs)}`);
  lines.push(`- Closed: ${formatUtcDateTime(report.closedMs)}`);
  lines.push(`- Time to close: ${formatDurationMs(report.timeToCloseMs)}`);
  if (report.description) lines.push('', '## Summary', '', report.description);
  lines.push('', report.kind === 'campaign' ? '## Incidents' : '## Remediation', '');
  for (const incident of report.incidents) {
    lines.push(`### ${incident.label}: ${incident.title}`, '', `${incident.severity} · ${incident.action}`, '');
    if (incident.steps.length === 0) lines.push('No steps were recorded.');
    incident.steps.forEach((step, index) => lines.push(`${index + 1}. ${step}`));
    lines.push('');
  }
  lines.push('## Timeline', '');
  if (report.timeline.length === 0) lines.push('No times were recorded.');
  for (const event of report.timeline) lines.push(`- ${formatUtcDateTime(event.atMs)}: ${event.text}`);
  lines.push('');
  return lines.join('\n');
}
